import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useNotifications } from "../contexts/NotificationsContext";
import { apiFetch } from "../lib/apiFetch";
import { useAccess } from "../contexts/AccessContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const { addNotification } = useNotifications() || {};
  const { refreshAccess } = useAccess() || {};

  useEffect(() => {
    const sessionId = params.get("session_id");

    async function fetchSession() {
      if (!sessionId) return;
      try {
        // Primary: call checkout confirm endpoint (supports GET)
        const res = await apiFetch(`/api/checkout/confirm?session_id=${encodeURIComponent(sessionId)}`);
        const json = await res.json();

        if (res.ok && json?.ok) {
          const plan = json.plan || json.planId || "studio";
          setInfo({ plan, interval: json.currentPeriodEnd ? "active" : "subscription" });
          window.dispatchEvent?.(
            new CustomEvent("mudo:toast", {
              detail: {
                type: "success",
                message: `You're now subscribed to ${String(plan).replace(/_/g, " ")}.`,
              },
            })
          );
          addNotification?.({
            world: "studio",
            type: "system-billing",
            title: "Subscription updated",
            body: `Plan ${String(plan).replace(/_/g, " ")} is active.`,
            target: { entity: "billing", route: "/settings" },
            severity: "success",
            meta: { sessionId },
          });
          return;
        }

        // Fallback: dev-only helper (if available)
        try {
          const resDev = await apiFetch(`/api/checkout/session?session_id=${encodeURIComponent(sessionId)}`);
          const jsonDev = await resDev.json();
          if (resDev.ok && jsonDev?.ok && jsonDev.session) {
            setInfo(jsonDev.session);
            window.dispatchEvent?.(
              new CustomEvent("mudo:toast", {
                detail: {
                  type: "success",
                  message: `You're now subscribed to ${String(jsonDev.session.plan).replace(/_/g, " ")} (${jsonDev.session.interval}).`,
                },
              })
            );
            addNotification?.({
              world: "studio",
              type: "system-billing",
              title: "Subscription updated",
              body: `Plan ${String(jsonDev.session.plan).replace(/_/g, " ")} is active.`,
              target: { entity: "billing", route: "/settings" },
              severity: "success",
              meta: { sessionId },
            });
            return;
          }
        } catch (_) {
          // ignore dev fallback errors
        }

        // Graceful fallback notification
        window.dispatchEvent?.(
          new CustomEvent("mudo:toast", { detail: { type: "success", message: "Subscription updated" } })
        );
        addNotification?.({
          world: "studio",
          type: "system-billing",
          title: "Subscription updated",
          body: "Your billing status was refreshed.",
          target: { entity: "billing", route: "/settings" },
          severity: "info",
        });
      } catch (err) {
        // still proceed gracefully
        window.dispatchEvent?.(
          new CustomEvent("mudo:toast", { detail: { type: "success", message: "Subscription updated" } })
        );
        addNotification?.({
          world: "studio",
          type: "system-billing",
          title: "Subscription updated",
          body: "Billing status refreshed.",
          target: { entity: "billing", route: "/settings" },
          severity: "info",
        });
      }
    }

    fetchSession();

    // Refresh access after checkout to sync access state
    (async () => {
      await refreshAccess?.();
    })();

    const t = setTimeout(() => navigate("/settings"), 2600);
    return () => clearTimeout(t);
  }, [params, navigate, refreshAccess]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center text-white">
      <div className="p-6 rounded-xl bg-white/5 border border-white/10 text-center max-w-lg">
        <div className="text-xl font-semibold mb-2">Thanks — your subscription is active.</div>
        {info ? (
          <div className="text-sm opacity-90 space-y-2">
            <div>
              Plan: <strong>{String(info.plan).replace(/_/g, " ")}</strong>
            </div>
            <div>
              Billing: <strong>{info.interval}</strong>
            </div>
              <div className="text-xs opacity-80">You’ll be redirected to Settings shortly.</div>
              <div className="mt-4 flex items-center justify-center gap-3">
                <button
                  className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                  onClick={() => {
                    try {
                      window.dispatchEvent(new CustomEvent("mudo:apply-plan-defaults", { detail: { plan: info.plan } }));
                      window.dispatchEvent(new CustomEvent("mudo:toast", { detail: { type: "success", message: "Applied plan defaults" } }));
                    } catch (e) {}
                  }}
                >
                  Apply plan defaults
                </button>
                <button
                  className="px-3 py-1.5 rounded border border-white/10 text-sm text-white/90 bg-transparent"
                  onClick={() => {
                    try {
                      window.dispatchEvent(new CustomEvent("mudo:apply-plan-defaults", { detail: { plan: info.plan } }));
                    } catch (e) {}
                    navigate("/settings");
                  }}
                >
                  Apply & open Settings
                </button>
              </div>
          </div>
        ) : (
          <div className="text-sm opacity-80">Redirecting you to Settings…</div>
        )}
      </div>
    </div>
  );
}
